import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useFormContext, useWatch } from 'react-hook-form'
import { Form, Col, Button } from 'react-bootstrap'

import { useVerificador, usePrecarga, useApiCall } from './Hooks'
import CargandoDatosLoader from 'components/CargandoDatosLoader'
import { mainRequest, validarCuitContribuyente } from 'utils/requests'
import CustomInput from './CustomInput'
import Directivos from './Directivos'
const today = new Date().toISOString().split('T')[0]

function DatosContribuyente() {
  const [errorCuit, setErrorCuit] = useState('')
  const [tipoPersona, setTipoPersona] = useState(null)
  const [content, setContent] = useState({})
  const [cuitJuridica, setCuitJuridica] = useState('')
  const { formState, control, trigger, errors, getValues, reset } =
    useFormContext()
  const location = useLocation()

  const storeData = useCallback(() => {
    if (location?.state?.visualizarDatos)
      return new Promise((resolve, _) => resolve(true)).then((res) => res)

    if (!formState.isValid) {
      trigger()
    }

    return mainRequest('/ddjj/alta/api/store-contribuyente', {
      method: 'POST',
      body: JSON.stringify({
        ...getValues(),
        tipo_persona: tipoPersona,
        modificarContribuyente: location?.state?.modificarDatos
      })
    }).then(({ status, content }) => content.success)
  }, [formState.isValid, trigger, getValues, tipoPersona, location])

  const tipo_persona = useWatch({
    control,
    name: 'tipo_persona'
  })

  useVerificador(storeData, true)
  const [loadingApi] = useApiCall('/ddjj/alta/api', setContent)
  const [loadingPrecarga] = usePrecarga(
    '/ddjj/alta/api/datos-contribuyente',
    'datos_contribuyente'
  )

  useEffect(() => {
    if (tipo_persona) {
      setTipoPersona(tipo_persona)
    }
  }, [tipo_persona, setTipoPersona])

  const validarCuit = (cuit) => {
    setErrorCuit('')
    validarCuitContribuyente(JSON.stringify({ cuit }))
      .then(({ status, content }) => {
        if (status === 200) {
          reset({ cuit: content.cuit, razon_social: content.razon_social })
          setTimeout(
            () => window.document.getElementById('inputRef').focus(),
            800
          )
        } else {
          setErrorCuit(content?.error)
        }
      })
      .catch((err) =>
        setErrorCuit(
          'Hubo un problema para validar la CUIT. Intente nuevamente más tarde.'
        )
      )
  }

  return (
    <CargandoDatosLoader cargando={loadingApi || loadingPrecarga}>
      <Form.Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label as="legend" column>
              ¿Tipo de persona?
            </Form.Label>
            <Form.Row className="justify-content-around px-5 py-2">
              <Form.Group className="custom-control custom-checkbox custom-control-inline">
                <Form.Control
                  disabled={
                    location?.state?.visualizarDatos ||
                    location?.state?.modificarDatos
                  }
                  type="checkbox"
                  id="check-humana"
                  className="custom-control-input"
                  value="F"
                  checked={tipoPersona === 'F'}
                  onChange={(e) => setTipoPersona(e.target.checked ? 'F' : 'J')}
                />
                <Form.Label
                  className="custom-control-label"
                  htmlFor="check-humana"
                >
                  Humana
                </Form.Label>
              </Form.Group>
              <Form.Group className="custom-control custom-checkbox custom-control-inline">
                <Form.Control
                  disabled={
                    location?.state?.visualizarDatos ||
                    location?.state?.modificarDatos
                  }
                  type="checkbox"
                  id="check-juridica"
                  className="custom-control-input"
                  value="J"
                  checked={tipoPersona === 'J'}
                  onChange={(e) => setTipoPersona(e.target.checked ? 'J' : 'F')}
                />
                <Form.Label
                  className="custom-control-label"
                  htmlFor="check-juridica"
                >
                  Jurídica
                </Form.Label>
              </Form.Group>
            </Form.Row>
          </Form.Group>
        </Col>
      </Form.Row>

      {tipoPersona === 'J' && (
        <>
          <h6 className="text-normal" style={{ color: '#dc3545' }}>
            Si usted está ingresando como representante, deberá corroborar la
            relación haciendo click en el botón VALIDAR.
          </h6>

          <Form.Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>CUIT de la Persona Jurídica</Form.Label>
                <Form.Control
                  as="input"
                  value={cuitJuridica}
                  onChange={(e) => setCuitJuridica(e.target.value)}
                  isValid={String(cuitJuridica).match(/^\d{11}$/)}
                  isInvalid={!String(cuitJuridica).match(/^\d{11}$/)}
                />
                <Form.Control.Feedback type="invalid">
                  La CUIT debe ser un número de 11 dígitos.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mt-4 ml-3">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => validarCuit(cuitJuridica)}
              >
                Validar
              </Button>
            </Col>
          </Form.Row>

          {errorCuit?.length > 0 && (
            <p style={{ color: '#FF0000' }}>{errorCuit}</p>
          )}
        </>
      )}

      {tipoPersona && (
        <>
          <h6 className="text-muted text-normal text-uppercase">
            Datos{' '}
            {tipoPersona === 'F'
              ? 'del contribuyente'
              : 'de la persona jurídica'}
          </h6>
          <hr className="mb-4" />

          <Form.Row>
            <CustomInput
              md={6}
              label="CUIT"
              placeholder="Ingrese la CUIT"
              type="number"
              name="cuit"
              errorPath={errors?.cuit}
              validation={{
                required: {
                  value: true,
                  message: 'Es necesario ingresar la CUIT'
                },
                minLength: {
                  value: 11,
                  message: 'La CUIT debe ser de 11 dígitos.'
                },
                maxLength: {
                  value: 11,
                  message: 'La CUIT debe ser de 11 dígitos.'
                },
                pattern: {
                  value: /^\d+$/,
                  message: 'Sólo puede incluir números.'
                }
              }}
              disabled
            />
            <CustomInput
              md={6}
              type="text"
              label="Apellido - Nombre o razón social"
              placeholder="Ingrese Apellido - Nombre o Razón Social"
              name="razon_social"
              errorPath={errors?.razon_social}
              validation={{
                required: {
                  value: true,
                  message: 'Es necesario ingresar el apellido o razón social.'
                },
                minLength: {
                  value: 1,
                  message: 'Debe poseer al menos un caracter.'
                }
              }}
              disabled
            />
          </Form.Row>

          <Form.Row>
            <CustomInput
              md={6}
              id="inputRef"
              as="select"
              label="Condición ante el I.V.A."
              placeholder="Seleccione una condición"
              name="condicion_iva"
              errorPath={errors.condicion_iva}
              validation={{
                required: true,
                validate: {
                  notDefault: (v) => v !== '-1'
                }
              }}
              options={content.categorias}
              disabled={
                location?.state?.visualizarDatos ||
                location?.state?.modificarDatos
              }
            />
            <CustomInput
              md={6}
              as="select"
              label="Condición ante Ingresos Brutos"
              placeholder="Seleccione una condición"
              name="condicion_ingresos_brutos"
              errorPath={errors?.condicion_ingresos_brutos}
              validation={{
                required: true,
                validate: {
                  notDefault: (v) => v !== '-1'
                }
              }}
              options={content.ingresos_brutos}
              disabled={location?.state?.visualizarDatos}
            />
          </Form.Row>

          <Form.Row>
            <CustomInput
              md={6}
              label="Domicilio Fiscal"
              placeholder="Ingrese calle"
              type="text"
              name="domicilio_calle"
              errorPath={errors?.domicilio_calle}
              validation={{
                required: {
                  value: true,
                  message: 'Es necesario ingresar la calle.'
                }
              }}
              disabled={
                location?.state?.visualizarDatos ||
                location?.state?.modificarDatos
              }
            />
            <CustomInput
              md={2}
              label="Número"
              placeholder="Número"
              type="number"
              name="domicilio_numero"
              errorPath={errors?.domicilio_numero}
              validation={{
                required: {
                  value: true,
                  message: 'Es necesario ingresar el número.'
                }
              }}
              disabled={
                location?.state?.visualizarDatos ||
                location?.state?.modificarDatos
              }
            />
            <CustomInput
              md={2}
              label="Piso"
              placeholder="Piso"
              name="domicilio_piso"
              errorPath={errors?.domicilio_piso}
              validation={{ required: false }}
              disabled={
                location?.state?.visualizarDatos ||
                location?.state?.modificarDatos
              }
            />
            <CustomInput
              md={2}
              label="Depto"
              placeholder="Departamento"
              type="text"
              name="domicilio_depto"
              errorPath={errors?.domicilio_depto}
              validation={{ required: false }}
              disabled={
                location?.state?.visualizarDatos ||
                location?.state?.modificarDatos
              }
            />
          </Form.Row>

          <Form.Row>
            <CustomInput
              md={6}
              label="Localidad"
              placeholder="Ingrese su localidad"
              type="text"
              name="domicilio_localidad"
              errorPath={errors?.domicilio_localidad}
              validation={{
                required: {
                  value: true,
                  message: 'Es necesario ingresar la localidad.'
                }
              }}
              disabled={
                location?.state?.visualizarDatos ||
                location?.state?.modificarDatos
              }
            />

            <CustomInput
              md={6}
              label="Código postal"
              placeholder="Ingrese su código postal"
              type="text"
              name="domicilio_cp"
              errorPath={errors?.domicilio_cp}
              validation={{
                required: {
                  value: true,
                  message: 'Es necesario ingresar el código postal.'
                }
              }}
              disabled={
                location?.state?.visualizarDatos ||
                location?.state?.modificarDatos
              }
            />
          </Form.Row>

          <Form.Row>
            <CustomInput
              md={6}
              label="Datos de contacto"
              placeholder="Ingrese correo electrónico"
              type="email"
              name="email"
              errorPath={errors?.email}
              validation={{
                required: {
                  value: true,
                  message: 'Es necesario ingresar un email.'
                },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Dirección email inválida.'
                }
              }}
              disabled={location?.state?.visualizarDatos}
            />

            <CustomInput
              md={6}
              label="Teléfono"
              placeholder="Ingrese un teléfono"
              type="number"
              name="telefono"
              errorPath={errors?.telefono}
              validation={{
                required: {
                  value: true,
                  message: 'Es necesario ingresar un teléfono.'
                },
                minLength: {
                  value: 1,
                  message: 'Es necesario ingresar un teléfono.'
                },
                pattern: {
                  value: /\d+/,
                  message: 'Sólo está permitido ingresar números.'
                }
              }}
              disabled={location?.state?.visualizarDatos}
            />
          </Form.Row>

          {tipoPersona === 'J' && (
            <>
              <h6 className="text-muted text-normal text-uppercase mt-3">
                Persona Jurídica
              </h6>
              <hr className="mb-4" />

              <Form.Row>
                <CustomInput
                  md={6}
                  label="Organismo donde está inscripta"
                  placeholder="Ingrese organismo donde está registrado"
                  type="text"
                  name="persona_juridica.organismo"
                  errorPath={errors?.persona_juridica?.organismo}
                  validation={{
                    required: {
                      value: true,
                      message: 'Es necesario ingresar el organismo.'
                    }
                  }}
                  disabled={
                    location?.state?.visualizarDatos ||
                    location?.state?.modificarDatos
                  }
                />
                <CustomInput
                  md={6}
                  label="N° de inscripción"
                  placeholder="N° Inscripción"
                  type="number"
                  name="persona_juridica.n_inscripcion"
                  errorPath={errors?.persona_juridica?.n_inscripcion}
                  validation={{
                    required: {
                      value: true,
                      message: 'Es necesario ingresar el número de inscripción.'
                    }
                  }}
                  disabled={
                    location?.state?.visualizarDatos ||
                    location?.state?.modificarDatos
                  }
                />
              </Form.Row>
              <Form.Row>
                <CustomInput
                  md={6}
                  label="Fecha de inscripción"
                  type="date"
                  name="persona_juridica.fecha_inscripcion"
                  errorPath={errors?.persona_juridica?.fecha_inscripcion}
                  validation={{
                    required: {
                      value: true,
                      message: 'Es necesario ingresar la fecha.'
                    }
                  }}
                  max={today}
                  disabled={
                    location?.state?.visualizarDatos ||
                    location?.state?.modificarDatos
                  }
                />
                <CustomInput
                  md={6}
                  label="Fecha de cierre del ejercicio económico"
                  type="date"
                  name="persona_juridica.fecha_cierre"
                  errorPath={errors?.persona_juridica?.fecha_cierre}
                  validation={{
                    required: {
                      value: true,
                      message: 'Es necesario ingresar la fecha.'
                    }
                  }}
                  disabled={
                    location?.state?.visualizarDatos ||
                    location?.state?.modificarDatos
                  }
                />
              </Form.Row>

              <Directivos />
            </>
          )}
        </>
      )}
    </CargandoDatosLoader>
  )
}

export default DatosContribuyente
